import React, { FC } from 'react';

import styles from './WinningToast.module.scss';
import cx from 'classnames';
import {
  getJackpotImagePathBySlug,
  TGreatWinSlugWithWinAmount,
  TLeaguesSlugWithWinAmount,
} from './lib';

import { DollarCoinIcon } from '@assets/icons';
import { Typography } from '@shared/Typography';
import { FullTicketTitle } from '@entities/jackpot';

interface LeaguesBadgesWrapperProps {
  children: React.ReactNode;
}

interface LeaguesBadgeProps {
  leaguesSlugWithWinAmount: TLeaguesSlugWithWinAmount;
}

const LeaguesBadgesWrapper = ({ children }: LeaguesBadgesWrapperProps) => {
  return <div className={styles.lBadgeWinnersBadgesWrapper}>{children}</div>;
};

const LeaguesBadge: FC<LeaguesBadgeProps> = ({ leaguesSlugWithWinAmount }) => {
  const { slug, amount } = leaguesSlugWithWinAmount;
  const slugLetter = slug.split('-')[1][0];

  const imagePath = getJackpotImagePathBySlug(slug);

  return (
    <div className={styles.lBadgeWrapper}>
      <div className={cx(styles.lBadge, styles[slugLetter])}>
        <div className={styles.lBadgeIconWrapper}>
          <img src={imagePath} alt="jackpot" className={styles.lBadgeIcon} />
          <Typography
            component="description2"
            variant="primary"
            className={styles.lSlugTitle}
          >
            {FullTicketTitle[slug]}
          </Typography>
        </div>
        <Typography
          component="description2"
          variant="primary"
          className={cx(styles.lTicketPrize, styles[slugLetter])}
        >
          {'$' + amount}
        </Typography>
      </div>
    </div>
  );
};

interface GreatWinBadgeProps {
  greatWinSlugWithWinAmount: TGreatWinSlugWithWinAmount;
}

const badgeImages = {
  Final1: 'images/winnerToast/final1.webp',
  Platinum: 'images/winnerToast/platinum.webp',
};

const GreatWinBadge = ({ greatWinSlugWithWinAmount }: GreatWinBadgeProps) => {
  const { slug, amount } = greatWinSlugWithWinAmount;
  return (
    <div className={styles.gwBadge}>
      {slug === 'Partner' || slug === 'ActivePlayers' ? (
        <div className={styles.gwBadgeName}>
          <Typography
            component="h0"
            variant="primary"
            className={cx(styles.gwTicketPrize, styles['Final1'])}
          >
            {slug === 'Partner' && 'Partners'}
            {slug === 'ActivePlayers' && 'Leaders'}
          </Typography>
        </div>
      ) : (
        <img src={badgeImages[slug]} alt={slug} className={styles.gwBadgeIcon} />
      )}

      <div className={cx(styles.gwPrizeWrapper, styles[slug])}>
        <img
          width={24}
          height={24}
          alt="dollar"
          src="images/dollar.webp"
          className={styles.mcIcon}
        />
        <Typography component="h1" variant="primary">
          {amount}
        </Typography>
      </div>
    </div>
  );
};

export interface WinningToastProps {
  greatWinSlugWithWinAmount?: TGreatWinSlugWithWinAmount;
  leaguesSlugsWithWinAmount?: TLeaguesSlugWithWinAmount[];
}

export const WinningToast: FC<WinningToastProps> = ({
  greatWinSlugWithWinAmount,
  leaguesSlugsWithWinAmount,
}) => {
  if (
    !greatWinSlugWithWinAmount &&
    (!leaguesSlugsWithWinAmount ||
      (leaguesSlugsWithWinAmount && leaguesSlugsWithWinAmount.length === 0))
  )
    return null;

  return (
    <div className={styles.wtContainer}>
      <div className={styles.wtTitleWrapper}>
        <DollarCoinIcon />
        <Typography component="h3" textTransform="uppercase" className={styles.wtTitle}>
          Congratulations, YOU WON!
        </Typography>
      </div>
      {greatWinSlugWithWinAmount && (
        <GreatWinBadge greatWinSlugWithWinAmount={greatWinSlugWithWinAmount} />
      )}
      {leaguesSlugsWithWinAmount && leaguesSlugsWithWinAmount.length > 0 && (
        <LeaguesBadgesWrapper>
          {leaguesSlugsWithWinAmount.map((leaguesSlugWithWinAmount, index) => (
            <LeaguesBadge
              leaguesSlugWithWinAmount={leaguesSlugWithWinAmount}
              key={index}
            />
          ))}
        </LeaguesBadgesWrapper>
      )}
    </div>
  );
};
